export type JobStatus =
  | 'AVAILABLE' | 'ACCEPTED' | 'SCHEDULED' | 'STARTED'
  | 'EN_ROUTE_TO_SELLER' | 'ARRIVED_PICKUP' | 'PICKUP_CHECKLIST_COMPLETE'
  | 'SELLER_SIGNATURE_COMPLETE' | 'LOADED_PHOTO_COMPLETE'
  | 'EN_ROUTE_TO_BUYER' | 'ARRIVED_DELIVERY'
  | 'DELIVERY_CHECKLIST_COMPLETE' | 'AWAITING_DELIVERY_PAYMENT'
  | 'BUYER_SIGNATURE_COMPLETE'
  | 'COMPLETED' | 'CANCELLED' | 'ESCALATED';

export interface WorkflowStep {
  status:            JobStatus;
  label:             string;
  endpoint:          string;
  action:            JobStatus;
  requiresChecklist?: boolean;
  requiresSignature?: boolean;
  requiresLoadedPhoto?: boolean;
  signaturePhase?:   'seller' | 'buyer';
}

export const JOB_WORKFLOW_STEPS: WorkflowStep[] = [
  { status: 'ACCEPTED',                    label: 'Start Job',               endpoint: 'start',              action: 'STARTED' },
  { status: 'SCHEDULED',                   label: 'Start Job',               endpoint: 'start',              action: 'STARTED' },
  { status: 'STARTED',                     label: 'En Route to Pickup',       endpoint: 'en-route-seller',    action: 'EN_ROUTE_TO_SELLER' },
  { status: 'EN_ROUTE_TO_SELLER',          label: 'Arrived at Pickup',        endpoint: 'arrive-pickup',      action: 'ARRIVED_PICKUP' },
  { status: 'ARRIVED_PICKUP',              label: 'Start Pickup Inspection',  endpoint: 'checklist/pickup',   action: 'PICKUP_CHECKLIST_COMPLETE', requiresChecklist: true },
  { status: 'PICKUP_CHECKLIST_COMPLETE',   label: 'Get Seller Signature',     endpoint: 'seller-signature',   action: 'SELLER_SIGNATURE_COMPLETE', requiresSignature: true, signaturePhase: 'seller' },
  { status: 'SELLER_SIGNATURE_COMPLETE',   label: 'Photo of Loaded Item',     endpoint: 'loaded-photo',       action: 'LOADED_PHOTO_COMPLETE', requiresLoadedPhoto: true },
  { status: 'LOADED_PHOTO_COMPLETE',       label: 'En Route to Delivery',     endpoint: 'en-route-buyer',     action: 'EN_ROUTE_TO_BUYER' },
  { status: 'EN_ROUTE_TO_BUYER',           label: 'Arrived at Delivery',      endpoint: 'arrive-delivery',    action: 'ARRIVED_DELIVERY' },
  { status: 'ARRIVED_DELIVERY',            label: 'Start Delivery Checklist', endpoint: 'checklist/delivery', action: 'DELIVERY_CHECKLIST_COMPLETE', requiresChecklist: true },
  { status: 'DELIVERY_CHECKLIST_COMPLETE', label: 'Get Buyer Signature',      endpoint: 'buyer-signature',    action: 'BUYER_SIGNATURE_COMPLETE', requiresSignature: true, signaturePhase: 'buyer' },
  // AWAITING_DELIVERY_PAYMENT (pay-on-delivery) removed — legacy status now
  // treated as a completed delivery in the UI.
];

/* ------------------------------ job types ---------------------------------- */

/**
 * Not every job is a pickup-then-delivery. An assembly-only visit has NO pickup leg at
 * all - the item is already at the customer's house - and a disassembly-only visit has no
 * delivery leg. Walking those through the full table asked the driver to "Arrive at
 * Pickup" for an address that does not exist, then to photograph a loaded item that was
 * never loaded.
 *
 * The backend sends `jobType` (and the flags below) with every job, so the app filters the
 * SAME table rather than keeping a second copy of the flow. Anything unknown falls back to
 * DELIVERY, which is the historic behaviour and what every existing job is.
 */
export type JobType = 'DELIVERY' | 'PICKUP_ONLY' | 'ASSEMBLY_ONLY' | 'DISASSEMBLY_ONLY';

/** Statuses belonging to each leg, used to filter the workflow for single-leg jobs. */
const PICKUP_LEG_STATUSES: JobStatus[] = [
  'EN_ROUTE_TO_SELLER', 'ARRIVED_PICKUP', 'PICKUP_CHECKLIST_COMPLETE', 'SELLER_SIGNATURE_COMPLETE',
];
const TRANSIT_AND_DELIVERY_STATUSES: JobStatus[] = [
  'LOADED_PHOTO_COMPLETE', 'EN_ROUTE_TO_BUYER', 'ARRIVED_DELIVERY', 'DELIVERY_CHECKLIST_COMPLETE',
];

/**
 * The workflow for a given job type.
 *
 * Both single-site types still finish on the step that actually completes a job, because
 * completion lives in the backend state machine and only the final signature triggers it:
 *   - ASSEMBLY_ONLY   goes straight to the site (the delivery address) and ends on the
 *                     buyer/customer signature.
 *   - DISASSEMBLY_ONLY and PICKUP_ONLY work at the pickup address, then jump to the
 *                     closing signature so the job can reach a terminal state at all.
 * Labels are rewritten so the driver reads "Arrived on Site", not "Arrived at Pickup".
 */
export function workflowFor(jobType?: string | null, opts?: { isStorage?: boolean }): WorkflowStep[] {
  const t = (String(jobType || 'DELIVERY').toUpperCase() as JobType);
  const asSteps = (arr: WorkflowStep[]): WorkflowStep[] => arr;
  if (t === 'DELIVERY') return JOB_WORKFLOW_STEPS;

  const relabel = (st: WorkflowStep, label?: string): WorkflowStep => (label ? { ...st, label } : st);
  const byStatus = (status: JobStatus) => JOB_WORKFLOW_STEPS.find(st => st.status === status)!;

  if (t === 'ASSEMBLY_ONLY') {
    // No pickup leg: start -> en route to site -> arrived -> checklist -> signature.
    return asSteps([
      relabel(byStatus('ACCEPTED')),
      relabel(byStatus('SCHEDULED')),
      { ...byStatus('STARTED'), label: 'En Route to Site', endpoint: 'en-route-buyer', action: 'EN_ROUTE_TO_BUYER' as JobStatus },
      relabel(byStatus('EN_ROUTE_TO_BUYER'), 'Arrived on Site'),
      relabel(byStatus('ARRIVED_DELIVERY'), 'Start Assembly Checklist'),
      relabel(byStatus('DELIVERY_CHECKLIST_COMPLETE'), 'Get Customer Signature'),
    ]).filter((st: WorkflowStep) => !PICKUP_LEG_STATUSES.includes(st.status));
  }

  if (t === 'DISASSEMBLY_ONLY' || t === 'PICKUP_ONLY') {
    const isPickup = t === 'PICKUP_ONLY';
    return asSteps([
      relabel(byStatus('ACCEPTED')),
      relabel(byStatus('SCHEDULED')),
      relabel(byStatus('STARTED'), isPickup ? 'En Route to Pickup' : 'En Route to Site'),
      relabel(byStatus('EN_ROUTE_TO_SELLER'), isPickup ? 'Arrived at Pickup' : 'Arrived on Site'),
      relabel(byStatus('ARRIVED_PICKUP'), isPickup ? 'Start Pickup Inspection' : 'Start Disassembly Checklist'),
      relabel(byStatus('PICKUP_CHECKLIST_COMPLETE'), 'Get Customer Signature'),
      // The closing step: nothing is delivered, so the job would otherwise never reach a
      // terminal status. Jump from the on-site signature straight to completion.
      // 🚨 A STORAGE PICKUP ENDS AT OUR UNIT, NOT WITH A BUYER. This step spreads the
      // delivery step, which requires the BUYER signature, so a storage driver standing at
      // the seller's house was asked for a buyer's signature that nobody could give. For a
      // store-to-sell job the closing step is the storage drop-off (photos in the unit + the
      // driver's own signature), which completes the job on the server.
      opts?.isStorage && isPickup
        ? { status: 'SELLER_SIGNATURE_COMPLETE' as JobStatus, label: 'Drop Off at Storage', endpoint: 'storage-drop-off', action: 'COMPLETED' as JobStatus }
        : { ...byStatus('DELIVERY_CHECKLIST_COMPLETE'), status: 'SELLER_SIGNATURE_COMPLETE' as JobStatus, label: isPickup ? 'Mark Collected' : 'Mark Complete' },
    ]).filter((st: WorkflowStep) => !TRANSIT_AND_DELIVERY_STATUSES.includes(st.status));
  }

  return JOB_WORKFLOW_STEPS;
}

